import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, DollarSign, ShoppingCart, Package, Calendar,
  Trophy, ArrowUpRight, ArrowDownRight, Store, Users, Target, ArrowLeftRight,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeeklyReport = () => {
  const { getAuthHeader } = useAuth();
  const { stores, storeName } = useStore();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeFilter, setStoreFilter] = useState('all');
  const [period, setPeriod] = useState('week');  // 'week' or 'month'
  const [staffReport, setStaffReport] = useState(null);
  const [staffWeeksBack, setStaffWeeksBack] = useState(0);  // 0=this week, 1=last week
  const [aliasDrafts, setAliasDrafts] = useState({});
  const [savingAliases, setSavingAliases] = useState(false);
  const [aliasVersion, setAliasVersion] = useState(0);
  const [emailingReport, setEmailingReport] = useState(false);
  const [swapRow, setSwapRow] = useState(null);
  const [swapCoverBy, setSwapCoverBy] = useState('');
  const [savingSwap, setSavingSwap] = useState(false);
  const [monthValue, setMonthValue] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (storeFilter !== 'all') params.append('store', storeFilter);
        let endpoint;
        if (period === 'month') {
          params.append('month', monthValue);
          endpoint = `${API_URL}/analytics/monthly-report?${params}`;
        } else {
          endpoint = `${API_URL}/analytics/weekly-report?${params}`;
        }
        const response = await axios.get(endpoint, { headers: getAuthHeader() });
        setReport(response.data);
      } catch (error) {
        toast.error(`Failed to load ${period === 'month' ? 'monthly' : 'weekly'} report`);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [getAuthHeader, storeFilter, period, monthValue]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const params = new URLSearchParams();
        if (storeFilter !== 'all') params.append('store', storeFilter);
        params.append('weeks_back', String(staffWeeksBack));
        const res = await axios.get(`${API_URL}/reports/staff-sales-weekly?${params}`, { headers: getAuthHeader() });
        setStaffReport(res.data);
      } catch (_) { /* non-blocking */ }
    };
    fetchStaff();
  }, [storeFilter, staffWeeksBack, getAuthHeader, aliasVersion]);

  const saveAliases = async () => {
    const aliases = Object.entries(aliasDrafts)
      .filter(([, roster]) => roster)
      .map(([sale_name, roster_name]) => ({ sale_name, roster_name }));
    if (!aliases.length) return;
    setSavingAliases(true);
    try {
      await axios.put(`${API_URL}/reports/staff-name-aliases`, { aliases }, { headers: getAuthHeader() });
      toast.success(`${aliases.length} name mapping${aliases.length > 1 ? 's' : ''} saved — sales re-attributed`);
      setAliasDrafts({});
      setAliasVersion(v => v + 1);
    } catch (e) {
      toast.error('Could not save name mappings');
    } finally {
      setSavingAliases(false);
    }
  };

  const emailReport = async () => {
    setEmailingReport(true);
    try {
      const res = await axios.post(`${API_URL}/reports/weekly-summary-email`, { weeks_back: staffWeeksBack }, { headers: getAuthHeader() });
      toast.success(`Report emailed to ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send email');
    } finally {
      setEmailingReport(false);
    }
  };

  const saveSwap = async () => {
    if (!swapRow || !swapCoverBy) return;
    setSavingSwap(true);
    try {
      const res = await axios.post(`${API_URL}/reports/swap-cover`, {
        date: swapRow.date,
        store: swapRow.store || (storeFilter !== 'all' ? storeFilter : ''),
        covered: swapRow.staff_name,
        cover_by: swapCoverBy,
      }, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Swap recorded');
      setSwapRow(null);
      setSwapCoverBy('');
      setAliasVersion(v => v + 1); // refetch the staff report
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record swap');
    } finally {
      setSavingSwap(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-lg">No data available</p>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: `$${report.summary.total_revenue.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Transactions',
      value: report.summary.total_transactions,
      icon: ShoppingCart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Units Sold',
      value: report.summary.total_units_sold,
      icon: Package,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Avg Transaction',
      value: `$${report.summary.avg_transaction_value.toFixed(2)}`,
      icon: TrendingUp,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50'
    }
  ];

  // Prepare chart data (oldest first). For monthly use day-of-month label.
  const chartData = [...report.daily_breakdown].sort((a, b) => a.date.localeCompare(b.date)).map(day => ({
    ...day,
    date: period === 'month'
      ? new Date(day.date).toLocaleDateString('en-NZ', { day: 'numeric' })
      : new Date(day.date).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric' }),
  }));

  return (
    <div className="space-y-6" data-testid="weekly-report-page">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            {period === 'month' ? 'Monthly Sales Report' : 'Weekly Sales Report'}
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {report.period} • {formatDate(report.start_date)} - {formatDate(report.end_date)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period toggle */}
          <div className="inline-flex rounded-md border border-slate-300 bg-white overflow-hidden" data-testid="period-toggle">
            <button
              className={`px-4 py-2 text-sm font-semibold transition-colors ${period === 'week' ? 'bg-orange-500 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => setPeriod('week')}
              data-testid="period-week"
            >
              This Week
            </button>
            <button
              className={`px-4 py-2 text-sm font-semibold transition-colors ${period === 'month' ? 'bg-orange-500 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => setPeriod('month')}
              data-testid="period-month"
            >
              By Month
            </button>
          </div>
          {/* Month picker only when on monthly */}
          {period === 'month' && (
            <input
              type="month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm"
              data-testid="month-picker"
            />
          )}
          {/* Store Filter */}
          <Select value={storeFilter} onValueChange={setStoreFilter}>
            <SelectTrigger className="w-[160px]" data-testid="store-filter">
              <Store className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
              <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <Card key={index} className="border rounded-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${card.bgColor}`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900">{card.value}</p>
              <p className="text-sm text-slate-500 uppercase tracking-wide mt-1">{card.title}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Sales Chart */}
        <Card className="border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Daily Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tickLine={false} 
                    axisLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                  />
                  <YAxis 
                    tickLine={false} 
                    axisLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    contentStyle={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                  <Bar 
                    dataKey="revenue" 
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={50}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Selling Products */}
        <Card className="border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Top Selling Products
            </CardTitle>
            <CardDescription>Most sold items this week</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-center">Units</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.top_products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No sales this week
                    </TableCell>
                  </TableRow>
                ) : (
                  report.top_products.map((product, index) => (
                    <TableRow key={product.product_id} data-testid={`top-product-${index}`}>
                      <TableCell className="text-center">
                        {index < 3 ? (
                          <Badge 
                            className={`${
                              index === 0 ? 'bg-yellow-500' : 
                              index === 1 ? 'bg-slate-400' : 
                              'bg-orange-400'
                            } text-white`}
                          >
                            {index + 1}
                          </Badge>
                        ) : (
                          <span className="text-slate-500">{index + 1}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{product.product_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-blue-50">
                          {product.units_sold}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">
                        ${product.revenue.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Daily Breakdown Table */}
      <Card className="border rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Daily Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-center">Transactions</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-right">Revenue</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-center">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((day, index) => {
                const prevDay = chartData[index - 1];
                const trend = prevDay ? day.revenue - prevDay.revenue : 0;
                
                return (
                  <TableRow key={day.date}>
                    <TableCell className="font-medium">
                      {new Date(report.daily_breakdown[report.daily_breakdown.length - 1 - index]?.date).toLocaleDateString('en-NZ', { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </TableCell>
                    <TableCell className="text-center">{day.transactions}</TableCell>
                    <TableCell className="text-right font-bold">${day.revenue.toFixed(2)}</TableCell>
                    <TableCell className="text-center">
                      {index > 0 && (
                        <span className={`flex items-center justify-center gap-1 ${
                          trend >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                          ${Math.abs(trend).toFixed(2)}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ==================== STAFF SALES — WEEKLY ==================== */}
      {staffReport && (
        <>
          <Card className="border-2 border-slate-300">
            <CardHeader className="bg-slate-50 border-b">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                    <Users className="w-5 h-5" /> Staff Sales — {staffWeeksBack === 0 ? 'This Week' : staffWeeksBack === 1 ? 'Last Week' : `${staffWeeksBack} weeks ago`}
                  </CardTitle>
                  <CardDescription>{formatDate(staffReport.week_start)} → {formatDate(staffReport.week_end)} · {storeFilter === 'orewa' ? 'Target $800/day' : 'Target $2,300/day (split shifts $1,150 each)'}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={emailReport}
                    disabled={emailingReport}
                    className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="email-weekly-summary-btn"
                    title="Also auto-sent every Monday 8am"
                  >
                    {emailingReport ? 'Sending…' : '✉ Email me this report'}
                  </button>
                  <Select value={String(staffWeeksBack)} onValueChange={(v) => setStaffWeeksBack(Number(v))}>
                    <SelectTrigger className="w-[160px]" data-testid="staff-weeks-back">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">This week</SelectItem>
                      <SelectItem value="1">Last week</SelectItem>
                      <SelectItem value="2">2 weeks ago</SelectItem>
                      <SelectItem value="3">3 weeks ago</SelectItem>
                      <SelectItem value="4">4 weeks ago</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Totals strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b">
                <div className="p-4 border-r">
                  <p className="text-xs uppercase text-slate-500 font-bold">Week Target</p>
                  <p className="text-2xl font-bold text-slate-900">${staffReport.totals.target.toLocaleString()}</p>
                </div>
                <div className="p-4 border-r">
                  <p className="text-xs uppercase text-slate-500 font-bold">Actual Sales</p>
                  <p className="text-2xl font-bold text-slate-900">${staffReport.totals.actual.toLocaleString()}</p>
                </div>
                <div className="p-4 border-r">
                  <p className="text-xs uppercase text-slate-500 font-bold">Diff vs Target</p>
                  <p className={`text-2xl font-bold ${staffReport.totals.diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {staffReport.totals.diff >= 0 ? '+' : ''}${staffReport.totals.diff.toLocaleString()}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs uppercase text-slate-500 font-bold">Shifts Hit Target</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {staffReport.totals.shifts_hit} / {staffReport.totals.shifts_total}
                  </p>
                </div>
              </div>

              {/* Fix Names — map unmatched PIN/sale names to roster staff */}
              {(() => {
                const unmatched = [...new Set((staffReport.rows || []).filter(r => r.is_unattributed && r.staff_name !== 'Unknown').map(r => r.staff_name))];
                const rosterNames = [...new Set((staffReport.per_staff || []).map(s => s.staff_name))];
                if (!unmatched.length || !rosterNames.length) return null;
                return (
                  <div className="p-4 border-b bg-amber-50" data-testid="fix-names-panel">
                    <p className="text-xs uppercase font-bold text-amber-700 mb-1">Fix names — these sale logins don't match any rostered staff</p>
                    <p className="text-xs text-amber-800 mb-3">Map each one to the right person and their sales will count towards their target (applies to past weeks too).</p>
                    <div className="flex flex-wrap gap-3 items-end">
                      {unmatched.map(name => (
                        <div key={name} className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">{name} →</span>
                          <select
                            value={aliasDrafts[name] || ''}
                            onChange={(e) => setAliasDrafts(prev => ({ ...prev, [name]: e.target.value }))}
                            className="h-8 rounded-md border border-amber-300 bg-white px-2 text-sm"
                            data-testid={`alias-select-${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                          >
                            <option value="">— pick staff —</option>
                            {rosterNames.map(rn => <option key={rn} value={rn}>{rn}</option>)}
                          </select>
                        </div>
                      ))}
                      <button
                        onClick={saveAliases}
                        disabled={savingAliases || !Object.values(aliasDrafts).some(Boolean)}
                        className="h-8 px-4 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
                        data-testid="save-aliases-btn"
                      >
                        {savingAliases ? 'Saving…' : 'Save mappings'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Per-staff summary */}
              {staffReport.per_staff && staffReport.per_staff.length > 0 && (
                <div className="p-4 border-b bg-slate-50">
                  <p className="text-xs uppercase font-bold text-slate-600 mb-2">Per-Staff Weekly Summary</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {staffReport.per_staff.map(s => {
                      const onTrack = s.diff >= 0;
                      return (
                        <div key={s.staff_name} className={`rounded-md border-2 p-3 ${onTrack ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid={`staff-summary-${s.staff_name}`}>
                          <div className="flex items-center justify-between">
                            <p className="font-bold text-slate-900">{s.staff_name}</p>
                            <Badge className={onTrack ? 'bg-emerald-600' : 'bg-amber-500'}>
                              {s.achieved_pct}%
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                            <div>
                              <p className="text-xs text-slate-500">Sold</p>
                              <p className="font-bold">${s.actual.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Target</p>
                              <p className="font-bold">${s.target.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Diff</p>
                              <p className={`font-bold ${onTrack ? 'text-emerald-700' : 'text-amber-700'}`}>{onTrack ? '+' : ''}${s.diff.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Shifts hit</p>
                              <p className="font-bold">{s.shifts_hit}/{s.shifts_total}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Per-shift table */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs">Day</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Staff</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Shift</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right">Target</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right">Sold</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right">Diff</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Sales</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Hit?</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffReport.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No sales recorded for this week.</TableCell></TableRow>
                  ) : staffReport.rows.map((r, i) => (
                    <TableRow key={i} className={r.off ? 'opacity-50' : r.upcoming ? 'opacity-40' : r.is_unattributed ? 'bg-amber-50' : (r.hit_target ? '' : (r.actual > 0 ? 'bg-amber-50/30' : ''))} data-testid={`staff-row-${i}`}>
                      <TableCell className="font-medium">{r.weekday} <span className="text-xs text-slate-500">{r.date.slice(5)}</span></TableCell>
                      <TableCell className="font-bold">
                        {r.staff_name}
                        {storeFilter === 'all' && r.store && r.store !== 'unknown' && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-normal">{r.store}</span>}
                        {r.covered_for && <span className="block text-[11px] font-normal text-teal-700">covering {r.covered_for}'s shift</span>}
                        {r.off && <span className="block text-[11px] font-normal text-slate-500">{r.covered_by ? `day off — covered by ${r.covered_by}` : 'day off'}</span>}
                        {r.no_shift && !r.is_unattributed && <span className="block text-[11px] font-normal text-slate-500">no rostered shift</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{r.shift_start ? `${r.shift_start}–${r.shift_end}` : '—'}</TableCell>
                      <TableCell className="text-right font-mono">${r.target.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-mono font-bold">${r.actual.toFixed(2)}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${r.diff >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{r.diff >= 0 ? '+' : ''}${r.diff.toFixed(2)}</TableCell>
                      <TableCell className="text-center text-sm">{r.sale_count}</TableCell>
                      <TableCell className="text-center">
                        {r.is_unattributed ? (
                          <Badge variant="outline" className="border-amber-400 text-amber-700">fix name</Badge>
                        ) : r.off ? (
                          <Badge variant="outline" className="border-slate-300 text-slate-500">off</Badge>
                        ) : r.upcoming ? (
                          <span className="text-slate-400 text-xs">upcoming</span>
                        ) : r.hit_target ? (
                          <Badge className="bg-emerald-600">✓</Badge>
                        ) : r.actual > 0 ? (
                          <Badge className="bg-amber-500">{r.achieved_pct}%</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center p-1">
                        {!r.no_shift && !r.covered_for && !r.is_unattributed && (
                          <button
                            onClick={() => { setSwapRow(r); setSwapCoverBy(''); }}
                            className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50 transition-colors"
                            title={`Record someone covering ${r.staff_name}'s shift`}
                            data-testid={`swap-btn-${i}`}
                          >
                            <ArrowLeftRight className="w-4 h-4" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Swap Quick-Log dialog */}
          <Dialog open={!!swapRow} onOpenChange={(open) => { if (!open) setSwapRow(null); }}>
            <DialogContent className="max-w-md" data-testid="swap-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ArrowLeftRight className="w-5 h-5 text-teal-600" /> Record a shift cover
                </DialogTitle>
                <DialogDescription>
                  {swapRow && (
                    <>Who covered <b>{swapRow.staff_name}</b>'s {swapRow.weekday} shift
                    {swapRow.shift_start ? ` (${swapRow.shift_start}–${swapRow.shift_end})` : ''}
                    {swapRow.store ? ` at ${swapRow.store.charAt(0).toUpperCase() + swapRow.store.slice(1)}` : ''}?
                    <span className="block mt-1 text-xs">This saves the swap to the Weekly Roster — {swapRow.staff_name} won't be marked as missing their target.</span></>
                  )}
                </DialogDescription>
              </DialogHeader>
              {swapRow && (
                <div className="space-y-4">
                  <Select value={swapCoverBy} onValueChange={setSwapCoverBy}>
                    <SelectTrigger data-testid="swap-cover-select">
                      <SelectValue placeholder="— pick who covered —" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set([
                        ...(staffReport?.rows || []).map(x => x.staff_name),
                        ...(staffReport?.per_staff || []).map(x => x.staff_name),
                        'Tina',
                      ])]
                        .filter(n => n && n !== swapRow.staff_name && !n.startsWith('⚠'))
                        .sort()
                        .map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setSwapRow(null)}
                      className="h-9 px-4 rounded-md border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      data-testid="swap-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveSwap}
                      disabled={!swapCoverBy || savingSwap}
                      className="h-9 px-4 rounded-md bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50"
                      data-testid="swap-confirm-btn"
                    >
                      {savingSwap ? 'Saving…' : `${swapCoverBy || 'They'} covered ${swapRow.staff_name}`}
                    </button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default WeeklyReport;
